import { useState, useEffect, useRef, useCallback } from 'react';
import { Canvas, FabricImage, Textbox, Shadow, Rect, Circle, Triangle, Polygon, FabricObject } from 'fabric';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FONTS = [
  'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
  'Courier New', 'Impact', 'Comic Sans MS', 'Trebuchet MS', 'Arial Black',
  'Palatino', 'Garamond', 'Bookman', 'Tahoma'
];

const GRAPHIC_ELEMENTS = [
  { id: 'rect', name: '▢ Rectangle', icon: '▢' },
  { id: 'circle', name: '○ Circle', icon: '○' },
  { id: 'triangle', name: '△ Triangle', icon: '△' },
  { id: 'star', name: '★ Star', icon: '★' },
  { id: 'arrow', name: '➤ Arrow', icon: '➤' },
  { id: 'badge', name: '⬡ Badge', icon: '⬡' }
];

function ImageEditor({ imageBase64, onSave, onClose, industry = 'General' }) {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const logoInputRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(!imageBase64);
  const [selectedObject, setSelectedObject] = useState(null);
  const [activeTab, setActiveTab] = useState('text');
  const [isGeneratingCaption, setIsGeneratingCaption] = useState(false);
  const [canvasObjects, setCanvasObjects] = useState({ texts: [], logos: [], elements: [] });
  
  // Text settings with shadow options
  const [textSettings, setTextSettings] = useState({
    text: 'Your Text Here',
    fontFamily: 'Arial',
    fontSize: 40,
    fill: '#FFFFFF',
    fontWeight: 'bold',
    fontStyle: 'normal',
    textAlign: 'center',
    // Shadow options
    shadowEnabled: false,
    shadowColor: '#000000',
    shadowBlur: 10,
    shadowOffsetX: 3,
    shadowOffsetY: 3
  });
  
  // Element/Graphic settings with advanced shadow
  const [elementSettings, setElementSettings] = useState({
    fillColor: '#8B5CF6',
    fillOpacity: 80,
    strokeColor: '#FFFFFF',
    strokeWidth: 2,
    rotation: 0,
    shadowEnabled: true,
    shadowColor: '#000000',
    shadowOpacity: 50,
    shadowBlur: 15,
    shadowOffsetX: 5,
    shadowOffsetY: 5,
    shadowDirection: 'bottom-right' // New: direction preset
  });
  
  // Shadow direction presets
  const SHADOW_DIRECTIONS = [
    { id: 'bottom-right', name: '↘ Bottom Right', offsetX: 5, offsetY: 5 },
    { id: 'bottom-left', name: '↙ Bottom Left', offsetX: -5, offsetY: 5 },
    { id: 'top-right', name: '↗ Top Right', offsetX: 5, offsetY: -5 },
    { id: 'top-left', name: '↖ Top Left', offsetX: -5, offsetY: -5 },
    { id: 'right', name: '→ Right', offsetX: 8, offsetY: 0 },
    { id: 'left', name: '← Left', offsetX: -8, offsetY: 0 },
    { id: 'bottom', name: '↓ Bottom', offsetX: 0, offsetY: 8 },
    { id: 'top', name: '↑ Top', offsetX: 0, offsetY: -8 },
    { id: 'seamless', name: '◉ Seamless (Glow)', offsetX: 0, offsetY: 0 }
  ];
  
  // Border style presets for image - line weights and styles
  const BORDER_WEIGHTS = [
    { id: 'none', name: 'None', width: 0, icon: '○' },
    { id: 'thin', name: 'Thin', width: 3, icon: '─' },
    { id: 'light', name: 'Light', width: 6, icon: '━' },
    { id: 'regular', name: 'Regular', width: 10, icon: '▬' },
    { id: 'medium', name: 'Medium', width: 16, icon: '▮' },
    { id: 'bold', name: 'Bold', width: 24, icon: '█' },
    { id: 'heavy', name: 'Heavy', width: 35, icon: '▓' },
    { id: 'ultra', name: 'Ultra', width: 50, icon: '██' }
  ];
  
  const BORDER_COLORS = [
    { id: 'white', name: 'White', color: '#FFFFFF' },
    { id: 'black', name: 'Black', color: '#000000' },
    { id: 'gray', name: 'Gray', color: '#6B7280' },
    { id: 'red', name: 'Red', color: '#EF4444' },
    { id: 'orange', name: 'Orange', color: '#F97316' },
    { id: 'yellow', name: 'Yellow', color: '#EAB308' },
    { id: 'green', name: 'Green', color: '#22C55E' },
    { id: 'blue', name: 'Blue', color: '#3B82F6' },
    { id: 'purple', name: 'Purple', color: '#8B5CF6' },
    { id: 'pink', name: 'Pink', color: '#EC4899' },
    { id: 'gold', name: 'Gold', color: '#FFD700' },
    { id: 'custom', name: 'Custom', color: null }
  ];
  
  // Border settings
  const [borderSettings, setBorderSettings] = useState({
    enabled: false,
    weight: 'none',
    color: '#FFFFFF',
    width: 0,
    customColor: '#8B5CF6'
  });
  
  // Image effects settings
  const [imageEffects, setImageEffects] = useState({
    brightness: 0,
    contrast: 0,
    saturation: 0,
    // Auto enhance
    autoEnhance: false
  });
  
  // Logo settings
  const [logoSettings, setLogoSettings] = useState({
    position: 'bottom-right',
    size: 15,
    opacity: 80
  });
  const [logoImage, setLogoImage] = useState(null);
  
  // AI Content settings
  const [aiContent, setAiContent] = useState({
    shortSummary: '',
    fullCaption: '',
    hashtags: '',
    platform: 'instagram'
  });
  const [showPreview, setShowPreview] = useState(false);
  
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [originalImageRef, setOriginalImageRef] = useState(null);
  
  // Export settings
  const [exportMultiplier, setExportMultiplier] = useState(2);
  const [exportFormat, setExportFormat] = useState('jpeg');
  
  // Update canvas objects list
  const updateObjectsList = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    
    const objects = canvas.getObjects();
    const texts = objects.filter(obj => obj.type === 'textbox' || obj.type === 'i-text');
    const logos = objects.filter(obj => obj.type === 'image' && obj.name === 'logo');
    const elements = objects.filter(obj => obj.name === 'graphic-element');
    setCanvasObjects({ texts, logos, elements });
  }, []);
  
  // Helper function to update settings from selected object
  // Helper function to update settings from selected object
  const updateSettingsFromObj = useCallback((obj) => {
    if (obj && (obj.type === 'textbox' || obj.type === 'i-text')) {
      setTextSettings(prev => ({
        ...prev,
        text: obj.text || 'Your Text Here',
        fontFamily: obj.fontFamily || 'Arial',
        fontSize: obj.fontSize || 40,
        fill: obj.fill || '#FFFFFF',
        fontWeight: obj.fontWeight || 'normal',
        fontStyle: obj.fontStyle || 'normal',
        textAlign: obj.textAlign || 'center'
      }));
    } else if (obj && obj.name === 'graphic-element') {
      setElementSettings(prev => ({
        ...prev,
        rotation: obj.angle || 0,
        shadowEnabled: !!obj.shadow
      }));
    }
  }, []);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || fabricRef.current) return;
    
    const canvas = new Canvas(canvasRef.current, {
      backgroundColor: '#1a1a2e',
      selection: true,
      preserveObjectStacking: true
    });

    fabricRef.current = canvas;

    canvas.on('selection:created', (e) => {
      if (e.selected && e.selected[0]) {
        setSelectedObject(e.selected[0]);
        updateSettingsFromObj(e.selected[0]);
      }
    });

    canvas.on('selection:updated', (e) => {
      if (e.selected && e.selected[0]) {
        setSelectedObject(e.selected[0]);
        updateSettingsFromObj(e.selected[0]);
      }
    });

    canvas.on('selection:cleared', () => {
      setSelectedObject(null);
    });

    canvas.on('object:added', updateObjectsList);
    canvas.on('object:removed', updateObjectsList);

    if (imageBase64) {
      FabricImage.fromURL(imageBase64, { crossOrigin: 'anonymous' })
        .then((img) => {
          const currentCanvas = fabricRef.current;
          if (!img || !currentCanvas) return;
          
          try {
            const maxWidth = 800;
            const maxHeight = 550;
            const imgWidth = img.width || 800;
            const imgHeight = img.height || 600;
            const scale = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
            
            img.scale(scale);
            
            const newWidth = Math.round(imgWidth * scale);
            const newHeight = Math.round(imgHeight * scale);
            
            try {
              currentCanvas.setDimensions({ width: newWidth, height: newHeight });
            } catch (dimErr) {
              console.warn('Could not set dimensions:', dimErr);
            }
            setCanvasSize({ width: newWidth, height: newHeight });
            
            img.set({ left: 0, top: 0, originX: 'left', originY: 'top' });
            currentCanvas.backgroundImage = img;
            setOriginalImageRef(img);
            currentCanvas.requestRenderAll();
            setIsLoaded(true);
          } catch (err) {
            console.error('Error setting up canvas:', err);
            setIsLoaded(true);
          }
        })
        .catch((err) => {
          console.error('Error loading image:', err);
          setIsLoaded(true);
        });
    }

    return () => {
      if (fabricRef.current) {
        fabricRef.current.dispose();
        fabricRef.current = null;
      }
    };
  }, [imageBase64, updateSettingsFromObj, updateObjectsList]);

  // Apply image effects
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !originalImageRef) return;
    
    const filters = [];
    
    // Auto enhance applies preset values
    const brightnessVal = imageEffects.autoEnhance ? 10 : imageEffects.brightness;
    const contrastVal = imageEffects.autoEnhance ? 15 : imageEffects.contrast;
    const saturationVal = imageEffects.autoEnhance ? 10 : imageEffects.saturation;
    
    if (brightnessVal !== 0) {
      filters.push(new (require('fabric').filters.Brightness)({ brightness: brightnessVal / 100 }));
    }
    if (contrastVal !== 0) {
      filters.push(new (require('fabric').filters.Contrast)({ contrast: contrastVal / 100 }));
    }
    if (saturationVal !== 0) {
      filters.push(new (require('fabric').filters.Saturation)({ saturation: saturationVal / 100 }));
    }
    
    originalImageRef.filters = filters;
    originalImageRef.applyFilters();
    canvas.requestRenderAll();
  }, [imageEffects, originalImageRef]);

  // Apply border using new borderSettings - using Line objects for precise border
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isLoaded) return;
    
    // Remove existing border lines
    const objects = canvas.getObjects();
    objects.forEach(obj => {
      if (obj.name && obj.name.startsWith('border-')) {
        canvas.remove(obj);
      }
    });
    
    if (borderSettings.enabled && borderSettings.width > 0) {
      const borderWidth = borderSettings.width;
      const w = canvas.getWidth();
      const h = canvas.getHeight();
      
      // Create 4 rectangles for each side of the border (more reliable than single stroke rect)
      const borderColor = borderSettings.color;
      
      // Top border
      const topBorder = new Rect({
        left: 0,
        top: 0,
        width: w,
        height: borderWidth,
        fill: borderColor,
        selectable: false,
        evented: false,
        name: 'border-top',
        hoverCursor: 'default'
      });
      
      // Bottom border
      const bottomBorder = new Rect({
        left: 0,
        top: h - borderWidth,
        width: w,
        height: borderWidth,
        fill: borderColor,
        selectable: false,
        evented: false,
        name: 'border-bottom',
        hoverCursor: 'default'
      });
      
      // Left border
      const leftBorder = new Rect({
        left: 0,
        top: 0,
        width: borderWidth,
        height: h,
        fill: borderColor,
        selectable: false,
        evented: false,
        name: 'border-left',
        hoverCursor: 'default'
      });
      
      // Right border
      const rightBorder = new Rect({
        left: w - borderWidth,
        top: 0,
        width: borderWidth,
        height: h,
        fill: borderColor,
        selectable: false,
        evented: false,
        name: 'border-right',
        hoverCursor: 'default'
      });
      
      canvas.add(topBorder, bottomBorder, leftBorder, rightBorder);
      
      // Bring borders to front
      canvas.bringObjectToFront(topBorder);
      canvas.bringObjectToFront(bottomBorder);
      canvas.bringObjectToFront(leftBorder);
      canvas.bringObjectToFront(rightBorder);
      
      // But keep text objects on top of border
      const allObjects = canvas.getObjects();
      allObjects.forEach(obj => {
        if (obj.type === 'textbox' || obj.type === 'i-text') {
          canvas.bringObjectToFront(obj);
        }
      });
    }
    
    canvas.requestRenderAll();
  }, [borderSettings.enabled, borderSettings.color, borderSettings.width, canvasSize, isLoaded]);

  // Add text to canvas with optional shadow
  const addText = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // Create shadow if enabled
    let shadowConfig = null;
    if (textSettings.shadowEnabled) {
      shadowConfig = new Shadow({
        color: textSettings.shadowColor,
        blur: textSettings.shadowBlur,
        offsetX: textSettings.shadowOffsetX,
        offsetY: textSettings.shadowOffsetY
      });
    }

    const text = new Textbox(textSettings.text, {
      left: canvasSize.width / 2,
      top: canvasSize.height / 2,
      originX: 'center',
      originY: 'center',
      fontFamily: textSettings.fontFamily,
      fontSize: textSettings.fontSize,
      fill: textSettings.fill,
      fontWeight: textSettings.fontWeight,
      fontStyle: textSettings.fontStyle,
      textAlign: textSettings.textAlign,
      shadow: shadowConfig,
      editable: true,
      width: canvasSize.width * 0.8,
      backgroundColor: undefined,
      padding: 0
    });

    canvas.add(text);
    canvas.bringObjectToFront(text); // Text always on top
    canvas.setActiveObject(text);
    canvas.requestRenderAll();
    setSelectedObject(text);
    updateObjectsList();
  }, [textSettings, canvasSize, updateObjectsList]);

  // Add graphic element to canvas
  const addElement = useCallback((elementId) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // Create shadow with opacity
    const shadowOpacity = elementSettings.shadowOpacity / 100;
    const shadowColorWithAlpha = elementSettings.shadowColor + Math.round(shadowOpacity * 255).toString(16).padStart(2, '0');
    
    const shadowConfig = elementSettings.shadowEnabled ? new Shadow({
      color: shadowColorWithAlpha,
      blur: elementSettings.shadowBlur,
      offsetX: elementSettings.shadowOffsetX,
      offsetY: elementSettings.shadowOffsetY
    }) : null;

    // Create fill color with opacity
    const fillOpacity = elementSettings.fillOpacity / 100;
    const fillColor = `rgba(${parseInt(elementSettings.fillColor.slice(1,3), 16)}, ${parseInt(elementSettings.fillColor.slice(3,5), 16)}, ${parseInt(elementSettings.fillColor.slice(5,7), 16)}, ${fillOpacity})`;

    let element;
    const baseProps = {
      left: canvasSize.width / 2,
      top: canvasSize.height / 2,
      originX: 'center',
      originY: 'center',
      fill: fillColor,
      stroke: elementSettings.strokeColor,
      strokeWidth: elementSettings.strokeWidth,
      shadow: shadowConfig,
      angle: elementSettings.rotation,
      name: 'graphic-element'
    };

    switch (elementId) {
      case 'rect':
        element = new Rect({ ...baseProps, width: 150, height: 100, rx: 10, ry: 10 });
        break;
      case 'circle':
        element = new Circle({ ...baseProps, radius: 60 });
        break;
      case 'triangle':
        element = new Triangle({ ...baseProps, width: 120, height: 100 });
        break;
      case 'star':
        // Create a 5-point star
        const starPoints = [];
        for (let i = 0; i < 10; i++) {
          const radius = i % 2 === 0 ? 60 : 30;
          const angle = (Math.PI / 5) * i - Math.PI / 2;
          starPoints.push({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius });
        }
        element = new Polygon(starPoints, { ...baseProps });
        break;
      case 'arrow':
        const arrowPoints = [
          { x: 0, y: -40 },
          { x: 40, y: 0 },
          { x: 20, y: 0 },
          { x: 20, y: 40 },
          { x: -20, y: 40 },
          { x: -20, y: 0 },
          { x: -40, y: 0 }
        ];
        element = new Polygon(arrowPoints, { ...baseProps });
        break;
      case 'badge':
        // Hexagon badge
        const hexPoints = [];
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 2;
          hexPoints.push({ x: Math.cos(angle) * 50, y: Math.sin(angle) * 50 });
        }
        element = new Polygon(hexPoints, { ...baseProps });
        break;
      default:
        element = new Rect({ ...baseProps, width: 100, height: 100 });
    }

    canvas.add(element);
    
    // Bring all text objects to front (text should always be on top of elements)
    const allObjects = canvas.getObjects();
    allObjects.forEach(obj => {
      if (obj.type === 'textbox' || obj.type === 'i-text') {
        canvas.bringObjectToFront(obj);
      }
    });
    
    canvas.setActiveObject(element);
    canvas.requestRenderAll();
    setSelectedObject(element);
    updateObjectsList();
  }, [elementSettings, canvasSize, updateObjectsList]);

  // Update element rotation
  const updateElementRotation = useCallback((angle) => {
    const canvas = fabricRef.current;
    if (!canvas || !selectedObject || selectedObject.name !== 'graphic-element') return;

    selectedObject.set('angle', angle);
    canvas.requestRenderAll();
    setElementSettings(prev => ({ ...prev, rotation: angle }));
  }, [selectedObject]);

  // Update selected text with shadow support
  const updateSelectedText = useCallback((property, value) => {
    const canvas = fabricRef.current;
    if (!canvas || !selectedObject) return;

    // Handle shadow updates
    if (property === 'shadowEnabled') {
      if (value) {
        selectedObject.set('shadow', new Shadow({
          color: textSettings.shadowColor,
          blur: textSettings.shadowBlur,
          offsetX: textSettings.shadowOffsetX,
          offsetY: textSettings.shadowOffsetY
        }));
      } else {
        selectedObject.set('shadow', null);
      }
    } else if (property.startsWith('shadow') && property !== 'shadowEnabled') {
      const currentShadow = selectedObject.shadow;
      if (currentShadow || textSettings.shadowEnabled) {
        const newShadow = new Shadow({
          color: property === 'shadowColor' ? value : textSettings.shadowColor,
          blur: property === 'shadowBlur' ? value : textSettings.shadowBlur,
          offsetX: property === 'shadowOffsetX' ? value : textSettings.shadowOffsetX,
          offsetY: property === 'shadowOffsetY' ? value : textSettings.shadowOffsetY
        });
        selectedObject.set('shadow', newShadow);
      }
    } else {
      selectedObject.set(property, value);
    }

    canvas.bringObjectToFront(selectedObject); // Keep text on top
    canvas.requestRenderAll();
    setTextSettings(prev => ({ ...prev, [property]: value }));
  }, [selectedObject, textSettings]);

  // Generate AI Content
  const generateAIContent = useCallback(async () => {
    if (!imageBase64) return;
    
    setIsGeneratingCaption(true);
    try {
      const base64Data = imageBase64.split(',')[1] || imageBase64;
      const response = await axios.post(`${API}/image/generate-ai-caption`, {
        image_base64: base64Data,
        industry: industry,
        platform: aiContent.platform,
        tone: 'engaging'
      });
      
      if (response.data.caption) {
        const fullCaption = response.data.caption;
        // Extract short summary (first sentence or 50 chars)
        const shortSummary = fullCaption.split(/[.!?]/)[0].trim().slice(0, 60) + '...';
        // Extract hashtags
        const hashtagMatch = fullCaption.match(/#\w+/g);
        const hashtags = hashtagMatch ? hashtagMatch.join(' ') : '#trending #viral';
        
        setAiContent(prev => ({
          ...prev,
          shortSummary,
          fullCaption: fullCaption.replace(/#\w+\s*/g, '').trim(),
          hashtags
        }));
        
        // Add short summary as overlay on image
        const canvas = fabricRef.current;
        if (canvas) {
          const text = new Textbox(shortSummary, {
            left: canvasSize.width / 2,
            top: canvasSize.height - 60,
            originX: 'center',
            originY: 'center',
            fontFamily: 'Arial',
            fontSize: 24,
            fill: '#FFFFFF',
            fontWeight: 'bold',
            textAlign: 'center',
            shadow: new Shadow({ color: '#000000', blur: 8, offsetX: 2, offsetY: 2 }),
            editable: true,
            width: canvasSize.width * 0.9,
            backgroundColor: 'rgba(0,0,0,0.6)',
            padding: 10
          });

          canvas.add(text);
          canvas.requestRenderAll();
          updateObjectsList();
        }
      }
    } catch (err) {
      console.error('Error generating caption:', err);
      alert('Failed to generate AI content. Please try again.');
    } finally {
      setIsGeneratingCaption(false);
    }
  }, [imageBase64, industry, aiContent.platform, canvasSize, updateObjectsList]);

  // Handle logo upload
  const handleLogoUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoImage(event.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Add logo to canvas
  const addLogoToCanvas = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas || !logoImage) return;
    
    FabricImage.fromURL(logoImage, { crossOrigin: 'anonymous' })
      .then((logo) => {
        if (!logo) return;
        
        const logoWidth = (canvasSize.width * logoSettings.size) / 100;
        const scale = logoWidth / (logo.width || 100);
        logo.scale(scale);
        
        let left, top;
        const padding = 15;
        const scaledWidth = (logo.width || 100) * scale;
        const scaledHeight = (logo.height || 100) * scale;
        
        switch (logoSettings.position) {
          case 'top-left':
            left = padding; top = padding; break;
          case 'top-right':
            left = canvasSize.width - scaledWidth - padding; top = padding; break;
          case 'bottom-left':
            left = padding; top = canvasSize.height - scaledHeight - padding; break;
          case 'bottom-right':
          default:
            left = canvasSize.width - scaledWidth - padding;
            top = canvasSize.height - scaledHeight - padding; break;
          case 'center':
            left = (canvasSize.width - scaledWidth) / 2;
            top = (canvasSize.height - scaledHeight) / 2; break;
        }
        
        logo.set({
          left, top,
          opacity: logoSettings.opacity / 100,
          originX: 'left',
          originY: 'top',
          name: 'logo'
        });
        
        canvas.add(logo);
        canvas.requestRenderAll();
        updateObjectsList();
      });
  }, [logoImage, logoSettings, canvasSize, updateObjectsList]);

  // Remove selected object
  const removeSelected = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas || !selectedObject) return;

    canvas.remove(selectedObject);
    setSelectedObject(null);
    canvas.requestRenderAll();
    updateObjectsList();
  }, [selectedObject, updateObjectsList]);

  // Remove specific object by reference
  const removeObject = useCallback((obj) => {
    const canvas = fabricRef.current;
    if (!canvas || !obj) return;

    canvas.remove(obj);
    if (selectedObject === obj) setSelectedObject(null);
    canvas.requestRenderAll();
    updateObjectsList();
  }, [selectedObject, updateObjectsList]);

  // Export canvas
  const exportImage = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.discardActiveObject();
    canvas.requestRenderAll();

    const dataURL = canvas.toDataURL({
      format: 'jpeg',
      quality: 0.95,
      multiplier: 2
    });

    onSave({
      image: dataURL,
      caption: aiContent.fullCaption,
      hashtags: aiContent.hashtags
    });
  }, [onSave, aiContent]);

  // Download image
  const downloadImage = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.discardActiveObject();
    canvas.requestRenderAll();

    const dataURL = canvas.toDataURL({
      format: exportFormat,
      quality: exportFormat === 'jpeg' ? 0.95 : 1,
      multiplier: exportMultiplier
    });

    const ext = exportFormat === 'jpeg' ? 'jpg' : 'png';
    const resLabel = exportMultiplier > 1 ? `_${exportMultiplier}x` : '';
    const link = document.createElement('a');
    link.download = `design${resLabel}-${Date.now()}.${ext}`;
    link.href = dataURL;
    link.click();
  }, [exportFormat, exportMultiplier]);

  // Render tab button
  const renderTabButton = (id, icon, label) => (
    <button
      key={id}
      onClick={() => setActiveTab(id)}
      className={`flex-1 py-2 px-1 text-xs font-medium rounded-lg transition ${
        activeTab === id ? 'bg-purple-600 text-white' : ''
      }`}
      style={{ 
        border: activeTab === id ? 'none' : '1px solid var(--border-color)',
        color: activeTab === id ? 'white' : 'var(--text-secondary)'
      }}
    >
      {icon} {label}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[100] flex" data-testid="image-editor-modal" onClick={(e) => e.stopPropagation()}>
      {/* Left Panel - Tools */}
      <div className="w-72 p-3 overflow-y-auto" style={{ background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)' }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold accent-text">🎨 Editor</h2>
          <button onClick={onClose} className="text-xl hover:opacity-70" style={{ color: 'var(--text-secondary)' }}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-3">
          {renderTabButton('text', '📝', 'Text')}
          {renderTabButton('effects', '✨', 'Effects')}
          {renderTabButton('ai', '🤖', 'AI')}
          {renderTabButton('logo', '🏷️', 'Logo')}
        </div>

        {/* Text Tab */}
        {activeTab === 'text' && (
          <div className="space-y-3">
            {/* Unified Text Section */}
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <h3 className="font-semibold mb-2 text-sm" style={{ color: 'var(--text-primary)' }}>📝 Text</h3>
              
              <div className="space-y-3">
                {/* Text Input */}
                <input
                  type="text"
                  value={textSettings.text}
                  onChange={(e) => {
                    setTextSettings(prev => ({ ...prev, text: e.target.value }));
                    if (selectedObject && (selectedObject.type === 'textbox' || selectedObject.type === 'i-text')) {
                      selectedObject.set('text', e.target.value);
                      fabricRef.current?.requestRenderAll();
                    }
                  }}
                  className="w-full px-2 py-1.5 rounded-lg text-sm"
                  placeholder="Enter text..."
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                />

                {/* Font & Size */}
                <div className="flex gap-2">
                  <select
                    value={textSettings.fontFamily}
                    onChange={(e) => {
                      setTextSettings(prev => ({ ...prev, fontFamily: e.target.value }));
                      if (selectedObject && (selectedObject.type === 'textbox' || selectedObject.type === 'i-text')) {
                        selectedObject.set('fontFamily', e.target.value);
                        fabricRef.current?.requestRenderAll();
                      }
                    }}
                    className="flex-1 px-2 py-1.5 rounded-lg text-xs"
                    style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  >
                    {FONTS.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={textSettings.fontSize}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 40;
                      setTextSettings(prev => ({ ...prev, fontSize: val }));
                      if (selectedObject && (selectedObject.type === 'textbox' || selectedObject.type === 'i-text')) {
                        selectedObject.set('fontSize', val);
                        fabricRef.current?.requestRenderAll();
                      }
                    }}
                    className="w-16 px-2 py-1.5 rounded-lg text-xs"
                    style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  />
                </div>

                {/* Color & Style Buttons */}
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={textSettings.fill}
                    onChange={(e) => {
                      setTextSettings(prev => ({ ...prev, fill: e.target.value }));
                      if (selectedObject && (selectedObject.type === 'textbox' || selectedObject.type === 'i-text')) {
                        selectedObject.set('fill', e.target.value);
                        fabricRef.current?.requestRenderAll();
                      }
                    }}
                    className="w-8 h-8 rounded cursor-pointer"
                    title="Text Color"
                  />
                  <button
                    onClick={() => {
                      const newWeight = textSettings.fontWeight === 'bold' ? 'normal' : 'bold';
                      setTextSettings(prev => ({ ...prev, fontWeight: newWeight }));
                      if (selectedObject && (selectedObject.type === 'textbox' || selectedObject.type === 'i-text')) {
                        selectedObject.set('fontWeight', newWeight);
                        fabricRef.current?.requestRenderAll();
                      }
                    }}
                    className={`w-8 h-8 rounded font-bold text-sm ${textSettings.fontWeight === 'bold' ? 'bg-purple-600' : ''}`}
                    style={{ border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  >B</button>
                  <button
                    onClick={() => {
                      const newStyle = textSettings.fontStyle === 'italic' ? 'normal' : 'italic';
                      setTextSettings(prev => ({ ...prev, fontStyle: newStyle }));
                      if (selectedObject && (selectedObject.type === 'textbox' || selectedObject.type === 'i-text')) {
                        selectedObject.set('fontStyle', newStyle);
                        fabricRef.current?.requestRenderAll();
                      }
                    }}
                    className={`w-8 h-8 rounded italic text-sm ${textSettings.fontStyle === 'italic' ? 'bg-purple-600' : ''}`}
                    style={{ border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  >I</button>
                  {['left', 'center', 'right'].map(align => (
                    <button
                      key={align}
                      onClick={() => {
                        setTextSettings(prev => ({ ...prev, textAlign: align }));
                        if (selectedObject && (selectedObject.type === 'textbox' || selectedObject.type === 'i-text')) {
                          selectedObject.set('textAlign', align);
                          fabricRef.current?.requestRenderAll();
                        }
                      }}
                      className={`w-8 h-8 rounded text-xs ${textSettings.textAlign === align ? 'bg-purple-600' : ''}`}
                      style={{ border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    >
                      {align === 'left' ? '◀' : align === 'center' ? '◆' : '▶'}
                    </button>
                  ))}
                </div>

                {/* Shadow Controls */}
                <div className="pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={textSettings.shadowEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setTextSettings(prev => ({ ...prev, shadowEnabled: enabled }));
                        if (selectedObject && (selectedObject.type === 'textbox' || selectedObject.type === 'i-text')) {
                          if (enabled) {
                            selectedObject.set('shadow', new Shadow({
                              color: textSettings.shadowColor,
                              blur: textSettings.shadowBlur,
                              offsetX: textSettings.shadowOffsetX,
                              offsetY: textSettings.shadowOffsetY
                            }));
                          } else {
                            selectedObject.set('shadow', null);
                          }
                          fabricRef.current?.requestRenderAll();
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Shadow</span>
                    {textSettings.shadowEnabled && (
                      <input
                        type="color"
                        value={textSettings.shadowColor}
                        onChange={(e) => {
                          setTextSettings(prev => ({ ...prev, shadowColor: e.target.value }));
                          if (selectedObject && selectedObject.shadow) {
                            selectedObject.set('shadow', new Shadow({
                              color: e.target.value,
                              blur: textSettings.shadowBlur,
                              offsetX: textSettings.shadowOffsetX,
                              offsetY: textSettings.shadowOffsetY
                            }));
                            fabricRef.current?.requestRenderAll();
                          }
                        }}
                        className="w-6 h-6 rounded cursor-pointer"
                      />
                    )}
                  </div>
                  {textSettings.shadowEnabled && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs w-10" style={{ color: 'var(--text-muted)' }}>Blur</span>
                        <input
                          type="range" min="0" max="30"
                          value={textSettings.shadowBlur}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setTextSettings(prev => ({ ...prev, shadowBlur: val }));
                            if (selectedObject && selectedObject.shadow) {
                              selectedObject.set('shadow', new Shadow({
                                color: textSettings.shadowColor,
                                blur: val,
                                offsetX: textSettings.shadowOffsetX,
                                offsetY: textSettings.shadowOffsetY
                              }));
                              fabricRef.current?.requestRenderAll();
                            }
                          }}
                          className="flex-1 accent-purple-500"
                        />
                        <span className="text-xs w-6" style={{ color: 'var(--text-muted)' }}>{textSettings.shadowBlur}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs w-10" style={{ color: 'var(--text-muted)' }}>X</span>
                        <input
                          type="range" min="-20" max="20"
                          value={textSettings.shadowOffsetX}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setTextSettings(prev => ({ ...prev, shadowOffsetX: val }));
                            if (selectedObject && selectedObject.shadow) {
                              selectedObject.set('shadow', new Shadow({
                                color: textSettings.shadowColor,
                                blur: textSettings.shadowBlur,
                                offsetX: val,
                                offsetY: textSettings.shadowOffsetY
                              }));
                              fabricRef.current?.requestRenderAll();
                            }
                          }}
                          className="flex-1 accent-purple-500"
                        />
                        <span className="text-xs w-6" style={{ color: 'var(--text-muted)' }}>{textSettings.shadowOffsetX}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs w-10" style={{ color: 'var(--text-muted)' }}>Y</span>
                        <input
                          type="range" min="-20" max="20"
                          value={textSettings.shadowOffsetY}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setTextSettings(prev => ({ ...prev, shadowOffsetY: val }));
                            if (selectedObject && selectedObject.shadow) {
                              selectedObject.set('shadow', new Shadow({
                                color: textSettings.shadowColor,
                                blur: textSettings.shadowBlur,
                                offsetX: textSettings.shadowOffsetX,
                                offsetY: val
                              }));
                              fabricRef.current?.requestRenderAll();
                            }
                          }}
                          className="flex-1 accent-purple-500"
                        />
                        <span className="text-xs w-6" style={{ color: 'var(--text-muted)' }}>{textSettings.shadowOffsetY}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Add Text Button */}
                <button
                  onClick={() => addText()}
                  className="w-full py-2.5 rounded-lg font-medium text-white transition hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }}
                >
                  ➕ Add Text to Canvas
                </button>
              </div>
            </div>

            {/* Elements Section - Graphics */}
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <h3 className="font-semibold mb-2 text-sm" style={{ color: 'var(--text-primary)' }}>🔷 Elements</h3>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {GRAPHIC_ELEMENTS.map(el => (
                  <button
                    key={el.id}
                    onClick={() => addElement(el.id)}
                    className="py-2 px-1 rounded-lg text-lg font-medium transition hover:bg-purple-500/20"
                    style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    title={el.name}
                  >
                    {el.icon}
                  </button>
                ))}
              </div>
              
              {/* Element Settings - shown when element is selected */}
              {selectedObject?.name === 'graphic-element' && (
                <div className="space-y-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-10" style={{ color: 'var(--text-secondary)' }}>Fill</span>
                    <input
                      type="color"
                      value={elementSettings.fillColor}
                      onChange={(e) => {
                        setElementSettings(prev => ({ ...prev, fillColor: e.target.value }));
                        if (selectedObject) {
                          const opacity = elementSettings.fillOpacity / 100;
                          const c = e.target.value;
                          selectedObject.set('fill', `rgba(${parseInt(c.slice(1,3), 16)}, ${parseInt(c.slice(3,5), 16)}, ${parseInt(c.slice(5,7), 16)}, ${opacity})`);
                          fabricRef.current?.requestRenderAll();
                        }
                      }}
                      className="w-6 h-6 rounded cursor-pointer"
                    />
                    <input
                      type="range" min="0" max="100"
                      value={elementSettings.fillOpacity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setElementSettings(prev => ({ ...prev, fillOpacity: val }));
                        if (selectedObject) {
                          const c = elementSettings.fillColor;
                          selectedObject.set('fill', `rgba(${parseInt(c.slice(1,3), 16)}, ${parseInt(c.slice(3,5), 16)}, ${parseInt(c.slice(5,7), 16)}, ${val/100})`);
                          fabricRef.current?.requestRenderAll();
                        }
                      }}
                      className="flex-1 accent-purple-500"
                    />
                    <span className="text-xs w-8" style={{ color: 'var(--text-muted)' }}>{elementSettings.fillOpacity}%</span>
                  </div>
                  
                  {/* Border / Borderless */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-10" style={{ color: 'var(--text-secondary)' }}>Border</span>
                    <input
                      type="color"
                      value={elementSettings.strokeColor}
                      onChange={(e) => {
                        setElementSettings(prev => ({ ...prev, strokeColor: e.target.value }));
                        if (selectedObject) {
                          selectedObject.set('stroke', e.target.value);
                          fabricRef.current?.requestRenderAll();
                        }
                      }}
                      className="w-6 h-6 rounded cursor-pointer"
                    />
                    <input
                      type="range" min="0" max="10"
                      value={elementSettings.strokeWidth}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setElementSettings(prev => ({ ...prev, strokeWidth: val }));
                        if (selectedObject) {
                          selectedObject.set('strokeWidth', val);
                          fabricRef.current?.requestRenderAll();
                        }
                      }}
                      className="flex-1 accent-purple-500"
                    />
                    <span className="text-xs w-6" style={{ color: 'var(--text-muted)' }}>{elementSettings.strokeWidth === 0 ? 'Off' : elementSettings.strokeWidth}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-10" style={{ color: 'var(--text-secondary)' }}>Rotate</span>
                    <input
                      type="range" min="0" max="360"
                      value={elementSettings.rotation}
                      onChange={(e) => updateElementRotation(parseInt(e.target.value))}
                      className="flex-1 accent-purple-500"
                    />
                    <span className="text-xs w-8" style={{ color: 'var(--text-muted)' }}>{elementSettings.rotation}°</span>
                  </div>
                  
                  {/* Advanced Shadow Controls */}
                  <div className="pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={elementSettings.shadowEnabled}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setElementSettings(prev => ({ ...prev, shadowEnabled: enabled }));
                          if (selectedObject) {
                            if (enabled) {
                              const opacity = elementSettings.shadowOpacity / 100;
                              const c = elementSettings.shadowColor;
                              selectedObject.set('shadow', new Shadow({
                                color: `rgba(${parseInt(c.slice(1,3), 16)}, ${parseInt(c.slice(3,5), 16)}, ${parseInt(c.slice(5,7), 16)}, ${opacity})`,
                                blur: elementSettings.shadowBlur,
                                offsetX: elementSettings.shadowOffsetX,
                                offsetY: elementSettings.shadowOffsetY
                              }));
                            } else {
                              selectedObject.set('shadow', null);
                            }
                            fabricRef.current?.requestRenderAll();
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Shadow</span>
                      {elementSettings.shadowEnabled && (
                        <input
                          type="color"
                          value={elementSettings.shadowColor}
                          onChange={(e) => {
                            setElementSettings(prev => ({ ...prev, shadowColor: e.target.value }));
                            if (selectedObject && selectedObject.shadow) {
                              const opacity = elementSettings.shadowOpacity / 100;
                              const c = e.target.value;
                              selectedObject.set('shadow', new Shadow({
                                color: `rgba(${parseInt(c.slice(1,3), 16)}, ${parseInt(c.slice(3,5), 16)}, ${parseInt(c.slice(5,7), 16)}, ${opacity})`,
                                blur: elementSettings.shadowBlur,
                                offsetX: elementSettings.shadowOffsetX,
                                offsetY: elementSettings.shadowOffsetY
                              }));
                              fabricRef.current?.requestRenderAll();
                            }
                          }}
                          className="w-5 h-5 rounded cursor-pointer"
                        />
                      )}
                    </div>
                    
                    {elementSettings.shadowEnabled && (
                      <div className="space-y-2">
                        {/* Shadow Direction Presets */}
                        <div>
                          <span className="text-xs block mb-1" style={{ color: 'var(--text-muted)' }}>Direction</span>
                          <select
                            value={elementSettings.shadowDirection}
                            onChange={(e) => {
                              const dir = SHADOW_DIRECTIONS.find(d => d.id === e.target.value);
                              if (dir) {
                                setElementSettings(prev => ({
                                  ...prev,
                                  shadowDirection: dir.id,
                                  shadowOffsetX: dir.offsetX,
                                  shadowOffsetY: dir.offsetY,
                                  shadowBlur: dir.id === 'seamless' ? 20 : prev.shadowBlur
                                }));
                                if (selectedObject && selectedObject.shadow) {
                                  const opacity = elementSettings.shadowOpacity / 100;
                                  const c = elementSettings.shadowColor;
                                  selectedObject.set('shadow', new Shadow({
                                    color: `rgba(${parseInt(c.slice(1,3), 16)}, ${parseInt(c.slice(3,5), 16)}, ${parseInt(c.slice(5,7), 16)}, ${opacity})`,
                                    blur: dir.id === 'seamless' ? 20 : elementSettings.shadowBlur,
                                    offsetX: dir.offsetX,
                                    offsetY: dir.offsetY
                                  }));
                                  fabricRef.current?.requestRenderAll();
                                }
                              }
                            }}
                            className="w-full px-2 py-1.5 rounded-lg text-xs"
                            style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                          >
                            {SHADOW_DIRECTIONS.map(dir => (
                              <option key={dir.id} value={dir.id}>{dir.name}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Blur & Opacity */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-12" style={{ color: 'var(--text-muted)' }}>Blur</span>
                          <input
                            type="range" min="0" max="40"
                            value={elementSettings.shadowBlur}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              setElementSettings(prev => ({ ...prev, shadowBlur: val }));
                              if (selectedObject && selectedObject.shadow) {
                                const opacity = elementSettings.shadowOpacity / 100;
                                const c = elementSettings.shadowColor;
                                selectedObject.set('shadow', new Shadow({
                                  color: `rgba(${parseInt(c.slice(1,3), 16)}, ${parseInt(c.slice(3,5), 16)}, ${parseInt(c.slice(5,7), 16)}, ${opacity})`,
                                  blur: val,
                                  offsetX: elementSettings.shadowOffsetX,
                                  offsetY: elementSettings.shadowOffsetY
                                }));
                                fabricRef.current?.requestRenderAll();
                              }
                            }}
                            className="flex-1 accent-purple-500"
                          />
                          <span className="text-xs w-6" style={{ color: 'var(--text-muted)' }}>{elementSettings.shadowBlur}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-12" style={{ color: 'var(--text-muted)' }}>Opacity</span>
                          <input
                            type="range" min="0" max="100"
                            value={elementSettings.shadowOpacity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              setElementSettings(prev => ({ ...prev, shadowOpacity: val }));
                              if (selectedObject && selectedObject.shadow) {
                                const c = elementSettings.shadowColor;
                                selectedObject.set('shadow', new Shadow({
                                  color: `rgba(${parseInt(c.slice(1,3), 16)}, ${parseInt(c.slice(3,5), 16)}, ${parseInt(c.slice(5,7), 16)}, ${val/100})`,
                                  blur: elementSettings.shadowBlur,
                                  offsetX: elementSettings.shadowOffsetX,
                                  offsetY: elementSettings.shadowOffsetY
                                }));
                                fabricRef.current?.requestRenderAll();
                              }
                            }}
                            className="flex-1 accent-purple-500"
                          />
                          <span className="text-xs w-8" style={{ color: 'var(--text-muted)' }}>{elementSettings.shadowOpacity}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Objects List */}
            {(canvasObjects.texts.length > 0 || canvasObjects.elements.length > 0) && (
              <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                <h3 className="font-semibold mb-2 text-sm" style={{ color: 'var(--text-primary)' }}>📋 Objects</h3>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {canvasObjects.texts.map((obj, i) => (
                    <div key={`text-${i}`} className="flex items-center justify-between p-1.5 rounded" style={{ background: 'var(--bg-primary)' }}>
                      <span className="text-xs truncate flex-1" style={{ color: 'var(--text-secondary)' }}>
                        📝 {obj.text?.slice(0, 15)}...
                      </span>
                      <button onClick={() => removeObject(obj)} className="text-red-400 hover:text-red-300 text-xs px-1">🗑️</button>
                    </div>
                  ))}
                  {canvasObjects.elements.map((obj, i) => (
                    <div key={`elem-${i}`} className="flex items-center justify-between p-1.5 rounded" style={{ background: 'var(--bg-primary)' }}>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>🔷 Element {i + 1}</span>
                      <button onClick={() => removeObject(obj)} className="text-red-400 hover:text-red-300 text-xs px-1">🗑️</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Border Section with Line Weight & Color */}
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <h3 className="font-semibold mb-3 text-sm" style={{ color: 'var(--text-primary)' }}>🔲 Image Border</h3>
              
              {/* Line Weight Selection */}
              <div className="mb-3">
                <span className="text-xs block mb-2" style={{ color: 'var(--text-secondary)' }}>Line Weight</span>
                <div className="grid grid-cols-4 gap-2">
                  {BORDER_WEIGHTS.map(weight => (
                    <button
                      key={weight.id}
                      onClick={() => {
                        if (weight.id === 'none') {
                          setBorderSettings(prev => ({ ...prev, enabled: false, weight: 'none', width: 0 }));
                        } else {
                          setBorderSettings(prev => ({ ...prev, enabled: true, weight: weight.id, width: weight.width }));
                        }
                      }}
                      className={`py-2 px-1 rounded-lg text-center transition flex flex-col items-center justify-center gap-1 ${borderSettings.weight === weight.id ? 'ring-2 ring-purple-500 bg-purple-500/20' : ''}`}
                      style={{ 
                        background: borderSettings.weight === weight.id ? 'rgba(139, 92, 246, 0.2)' : 'var(--bg-primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      <span className="text-lg" style={{ color: 'var(--text-primary)' }}>{weight.icon}</span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{weight.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Color Selection - Only show when border is enabled */}
              {borderSettings.enabled && (
                <>
                  <div className="mb-3">
                    <span className="text-xs block mb-2" style={{ color: 'var(--text-secondary)' }}>Border Color</span>
                    <div className="grid grid-cols-6 gap-2">
                      {BORDER_COLORS.map(colorOption => (
                        <button
                          key={colorOption.id}
                          onClick={() => {
                            if (colorOption.id === 'custom') {
                              setBorderSettings(prev => ({ ...prev, color: prev.customColor }));
                            } else {
                              setBorderSettings(prev => ({ ...prev, color: colorOption.color }));
                            }
                          }}
                          className={`w-full aspect-square rounded-lg transition ${borderSettings.color === colorOption.color || (colorOption.id === 'custom' && !BORDER_COLORS.slice(0, -1).some(c => c.color === borderSettings.color)) ? 'ring-2 ring-purple-500 ring-offset-1' : ''}`}
                          style={{ 
                            background: colorOption.id === 'custom' 
                              ? `linear-gradient(135deg, #FF6B6B, #4ECDC4, #45B7D1, #96CEB4, #FFEAA7, #DDA0DD)` 
                              : colorOption.color,
                            border: colorOption.color === '#FFFFFF' ? '1px solid var(--border-color)' : 'none'
                          }}
                          title={colorOption.name}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Custom Color Picker */}
                  <div className="flex items-center gap-3 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Custom</span>
                    <input
                      type="color"
                      value={borderSettings.customColor}
                      onChange={(e) => setBorderSettings(prev => ({ ...prev, customColor: e.target.value, color: e.target.value }))}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                    <div className="flex-1">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Width</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="range" min="1" max="60"
                          value={borderSettings.width}
                          onChange={(e) => setBorderSettings(prev => ({ ...prev, width: parseInt(e.target.value) }))}
                          className="flex-1 accent-purple-500"
                        />
                        <span className="text-xs w-10" style={{ color: 'var(--text-muted)' }}>{borderSettings.width}px</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Effects Tab */}
        {activeTab === 'effects' && (
          <div className="space-y-3">
            {/* Auto AI Enhancement */}
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>🪄 Auto AI Enhance</h3>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>One-click improvement</p>
                </div>
                <input
                  type="checkbox"
                  checked={imageEffects.autoEnhance}
                  onChange={(e) => setImageEffects(prev => ({ ...prev, autoEnhance: e.target.checked }))}
                  className="rounded w-5 h-5"
                />
              </div>
            </div>

            {/* Manual Adjustments */}
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <h3 className="font-semibold mb-2 text-sm" style={{ color: 'var(--text-primary)' }}>🎨 Adjustments</h3>
              <div className="space-y-2">
                {[
                  { key: 'brightness', label: 'Brightness' },
                  { key: 'contrast', label: 'Contrast' },
                  { key: 'saturation', label: 'Saturation' }
                ].map(({ key, label }) => (
                  <div key={key}>
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{imageEffects[key]}</span>
                    </div>
                    <input
                      type="range" min="-100" max="100"
                      value={imageEffects[key]}
                      onChange={(e) => setImageEffects(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                      className="w-full accent-purple-500"
                      disabled={imageEffects.autoEnhance}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AI Tab */}
        {activeTab === 'ai' && (
          <div className="space-y-3">
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <h3 className="font-semibold mb-2 text-sm" style={{ color: 'var(--text-primary)' }}>🤖 AI Content</h3>
              <select
                value={aiContent.platform}
                onChange={(e) => setAiContent(prev => ({ ...prev, platform: e.target.value }))}
                className="w-full px-2 py-1.5 rounded-lg text-sm mb-2"
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              >
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="twitter">Twitter/X</option>
                <option value="linkedin">LinkedIn</option>
              </select>
              <button
                onClick={generateAIContent}
                disabled={isGeneratingCaption}
                className="w-full py-2.5 rounded-lg font-medium text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }}
              >
                {isGeneratingCaption ? '⏳ Analyzing...' : '✨ Generate AI Content'}
              </button>
            </div>

            {aiContent.fullCaption && (
              <>
                <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                  <h3 className="font-semibold mb-1 text-sm" style={{ color: 'var(--text-primary)' }}>📝 Caption</h3>
                  <textarea
                    value={aiContent.fullCaption}
                    onChange={(e) => setAiContent(prev => ({ ...prev, fullCaption: e.target.value }))}
                    rows={3}
                    className="w-full px-2 py-1.5 rounded-lg text-xs resize-none"
                    style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  />
                </div>

                <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                  <h3 className="font-semibold mb-1 text-sm" style={{ color: 'var(--text-primary)' }}>#️⃣ Hashtags</h3>
                  <textarea
                    value={aiContent.hashtags}
                    onChange={(e) => setAiContent(prev => ({ ...prev, hashtags: e.target.value }))}
                    rows={2}
                    className="w-full px-2 py-1.5 rounded-lg text-xs resize-none"
                    style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--accent-primary)' }}
                  />
                </div>

                <button
                  onClick={() => setShowPreview(true)}
                  className="w-full py-2 rounded-lg font-medium"
                  style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--accent-primary)', color: 'var(--accent-primary)' }}
                >
                  👁️ Preview Posts
                </button>
              </>
            )}
          </div>
        )}

        {/* Logo Tab */}
        {activeTab === 'logo' && (
          <div className="space-y-3">
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <h3 className="font-semibold mb-2 text-sm" style={{ color: 'var(--text-primary)' }}>🏷️ Watermark</h3>
              
              <input
                type="file"
                ref={logoInputRef}
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              
              {!logoImage ? (
                <button
                  onClick={() => logoInputRef.current?.click()}
                  className="w-full py-3 rounded-lg font-medium border-2 border-dashed transition hover:bg-purple-500/10"
                  style={{ borderColor: 'var(--accent-primary)', color: 'var(--text-secondary)' }}
                >
                  📁 Upload Logo
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                    <img src={logoImage} alt="Logo" className="w-10 h-10 object-contain rounded" />
                    <span className="text-xs flex-1" style={{ color: 'var(--text-primary)' }}>Logo ready</span>
                    <button onClick={() => setLogoImage(null)} className="text-red-400 text-xs">✕</button>
                  </div>
                  
                  <select
                    value={logoSettings.position}
                    onChange={(e) => setLogoSettings(prev => ({ ...prev, position: e.target.value }))}
                    className="w-full px-2 py-1.5 rounded-lg text-xs"
                    style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  >
                    <option value="top-left">↖ Top Left</option>
                    <option value="top-right">↗ Top Right</option>
                    <option value="bottom-left">↙ Bottom Left</option>
                    <option value="bottom-right">↘ Bottom Right</option>
                    <option value="center">⊙ Center</option>
                  </select>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Size</span>
                    <input
                      type="range" min="5" max="50"
                      value={logoSettings.size}
                      onChange={(e) => setLogoSettings(prev => ({ ...prev, size: parseInt(e.target.value) }))}
                      className="flex-1 accent-purple-500"
                    />
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{logoSettings.size}%</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Opacity</span>
                    <input
                      type="range" min="10" max="100"
                      value={logoSettings.opacity}
                      onChange={(e) => setLogoSettings(prev => ({ ...prev, opacity: parseInt(e.target.value) }))}
                      className="flex-1 accent-purple-500"
                    />
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{logoSettings.opacity}%</span>
                  </div>
                  
                  <button
                    onClick={addLogoToCanvas}
                    className="w-full py-2 rounded-lg font-medium text-white"
                    style={{ background: 'var(--accent-gradient)' }}
                  >
                    ➕ Add Logo
                  </button>
                </div>
              )}
            </div>

            {/* Logo Objects List */}
            {canvasObjects.logos.length > 0 && (
              <div className="p-3 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                <h3 className="font-semibold mb-2 text-sm" style={{ color: 'var(--text-primary)' }}>📋 Logos Added</h3>
                <div className="space-y-1">
                  {canvasObjects.logos.map((obj, i) => (
                    <div key={i} className="flex items-center justify-between p-1.5 rounded" style={{ background: 'var(--bg-primary)' }}>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Logo {i + 1}</span>
                      <button
                        onClick={() => removeObject(obj)}
                        className="text-red-400 hover:text-red-300 text-xs px-1"
                      >🗑️</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Selected */}
        {selectedObject && (
          <button
            onClick={removeSelected}
            className="w-full mt-3 py-2 rounded-lg font-medium text-red-400 border border-red-500/50 hover:bg-red-500/20"
          >
            🗑️ Delete Selected
          </button>
        )}
      </div>

      {/* Center - Canvas */}
      <div className="flex-1 flex flex-col items-center justify-center p-4" style={{ background: '#0a0a0f' }}>
        {!isLoaded && (
          <div className="text-center mb-4" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
        )}
        <div className="rounded-xl overflow-hidden shadow-2xl" style={{ border: '2px solid var(--accent-primary)' }}>
          <canvas ref={canvasRef} width={800} height={550} data-testid="fabric-canvas" />
        </div>
        <p className="mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>
          Click to select • Drag to move • Corners to resize
        </p>
      </div>

      {/* Right Panel - Export & Preview */}
      <div className="w-64 p-3 flex flex-col" style={{ background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border-color)' }}>
        <h3 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Export</h3>
        
        {/* Export Settings */}
        <div className="mb-3 p-2 rounded-lg space-y-2" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Resolution</label>
            <div className="flex gap-1">
              {[{v:1,l:'1x'},{v:2,l:'2x'},{v:3,l:'3x'}].map(opt => (
                <button
                  key={opt.v}
                  data-testid={`export-res-${opt.v}x`}
                  onClick={() => setExportMultiplier(opt.v)}
                  className={`flex-1 py-1.5 rounded text-xs font-medium transition ${exportMultiplier === opt.v ? 'bg-purple-600 text-white' : ''}`}
                  style={exportMultiplier !== opt.v ? { background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' } : {}}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              {canvasSize.width * exportMultiplier} × {canvasSize.height * exportMultiplier} px
            </div>
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Format</label>
            <div className="flex gap-1">
              {[{v:'jpeg',l:'JPG'},{v:'png',l:'PNG'}].map(opt => (
                <button
                  key={opt.v}
                  data-testid={`export-fmt-${opt.v}`}
                  onClick={() => setExportFormat(opt.v)}
                  className={`flex-1 py-1.5 rounded text-xs font-medium transition ${exportFormat === opt.v ? 'bg-purple-600 text-white' : ''}`}
                  style={exportFormat !== opt.v ? { background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' } : {}}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="space-y-2 flex-1">
          <button
            data-testid="save-continue-btn"
            onClick={exportImage}
            className="w-full py-3 rounded-lg font-medium text-white"
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
          >
            ✅ Save & Continue
          </button>
          
          <button
            data-testid="download-btn"
            onClick={downloadImage}
            className="w-full py-3 rounded-lg font-medium"
            style={{ background: 'var(--accent-gradient)', color: 'white' }}
          >
            Download ({exportMultiplier}x {exportFormat.toUpperCase()})
          </button>

          {aiContent.fullCaption && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${aiContent.fullCaption}\n\n${aiContent.hashtags}`);
                alert('Caption & hashtags copied!');
              }}
              className="w-full py-2 rounded-lg font-medium text-sm"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
            >
              📋 Copy Caption
            </button>
          )}
        </div>

        <div className="mt-auto pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg text-sm"
            style={{ border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Social Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[110]" onClick={() => setShowPreview(false)}>
          <div className="max-w-4xl w-full p-6 rounded-2xl" style={{ background: 'var(--bg-secondary)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>👁️ Social Preview</h3>
              <button onClick={() => setShowPreview(false)} className="text-2xl" style={{ color: 'var(--text-secondary)' }}>×</button>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Instagram Preview */}
              <div className="rounded-xl p-4" style={{ background: '#000', border: '1px solid #333' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600" />
                  <span className="text-white text-sm font-semibold">your_brand</span>
                </div>
                <div className="aspect-square bg-gray-800 rounded overflow-hidden mb-3">
                  <canvas ref={canvasRef} className="w-full h-full object-cover" style={{ display: 'none' }} />
                  <img src={fabricRef.current?.toDataURL()} alt="Preview" className="w-full h-full object-cover" />
                </div>
                <p className="text-white text-sm">{aiContent.fullCaption?.slice(0, 100)}...</p>
                <p className="text-blue-400 text-xs mt-1">{aiContent.hashtags?.slice(0, 60)}...</p>
              </div>

              {/* Facebook Preview */}
              <div className="rounded-xl p-4" style={{ background: '#242526', border: '1px solid #3a3b3c' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600" />
                  <div>
                    <span className="text-white text-sm font-semibold block">Your Brand</span>
                    <span className="text-gray-400 text-xs">Just now • 🌐</span>
                  </div>
                </div>
                <p className="text-white text-sm mb-3">{aiContent.fullCaption?.slice(0, 150)}...</p>
                <div className="aspect-video bg-gray-700 rounded overflow-hidden">
                  <img src={fabricRef.current?.toDataURL()} alt="Preview" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={downloadImage}
                className="flex-1 py-3 rounded-lg font-medium text-white"
                style={{ background: 'var(--accent-gradient)' }}
              >
                📥 Download for Posting
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${aiContent.fullCaption}\n\n${aiContent.hashtags}`);
                  alert('Copied!');
                }}
                className="flex-1 py-3 rounded-lg font-medium"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              >
                📋 Copy Caption
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageEditor;
